// Per-device detail. Phase 2: shows overview + owner + location + attached
// SIP accounts, and lets org_admin assign/unassign/place/attach-account/
// remove-account/delete. Handset editing (DP-family per-line mapping) is
// deferred to Phase 3 alongside the cloud→agent JWT bridge.
//
// Deleting a device requires typing `delete` (case-insensitive, the literal
// word — never the MAC) per feedback_delete_confirm_word.md. The reversible
// flows here (unassign, remove placement, detach account) pass
// destructive={false} for a plain yes/no.
//
// This page used to carry its OWN copy of the confirm dialog with the typing
// check hardwired off (`matches = true`), so the header claimed a rule the code
// didn't follow and Delete Device never asked for the word. It uses the shared
// component now.

import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { fmtDateTime } from '../lib/time.js';
import { useAuth } from '../hooks/useAuth.js';
import { canOperate, canAdmin } from '../lib/roles.js';
import LoadingSpinner from '../components/shared/LoadingSpinner.jsx';
import StatusBadge from '../components/shared/StatusBadge.jsx';
import ConfirmDeleteDialog from '../components/shared/ConfirmDeleteDialog.jsx';
import ActionMenu from '../components/shared/ActionMenu.jsx';
import DeviceSegmentCard from '../components/devices/DeviceSegmentCard.jsx';
import HandsetMatrix from '../components/devices/HandsetMatrix.jsx';
import useHashTab from '../hooks/useHashTab.js';
import useBackTo from '../hooks/useBackTo.js';

// Module scope: useHashTab memoises on this.
const DEVICE_TABS = ['overview', 'lines', 'gdms'];

// ------- Field row -------
function Field({ label, value }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900">{value}</dd>
    </div>
  );
}

// ------- Sub-forms -------
// Pick the owning customer BY NAME.
//
// This was a numeric "Subscriber ID" text box — you had to know the database id
// by heart, so in practice the assignment didn't work. Same shape as the
// location picker: search, then click.
function AssignSubscriberForm({ onSubmit, onCancel, isPending, error }) {
  const [picked, setPicked] = useState('');
  const [q, setQ] = useState('');

  const subsQ = useQuery({
    queryKey: ['subscribers'],
    queryFn: () => api.get('/subscribers'),
  });
  const all = Array.isArray(subsQ.data)
    ? subsQ.data
    : Array.isArray(subsQ.data?.rows) ? subsQ.data.rows : [];

  const needle = q.trim().toLowerCase();
  const matches = needle
    ? all.filter((c) => [c.name, c.display_name, c.billingAccount, c.billing_account, c.extension]
        .filter(Boolean).join(' ').toLowerCase().includes(needle))
    : all;

  return (
    <div className="mt-3 rounded-md border border-blue-200 bg-blue-50/40 p-3">
      <label htmlFor="dev-sub-search" className="block text-xs font-medium text-gray-700">Customer</label>
      <input
        id="dev-sub-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by name or billing account…"
        autoFocus
        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
      />

      <div className="mt-2 max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white">
        {subsQ.isLoading ? (
          <p className="px-3 py-4 text-center text-sm text-gray-500">Loading customers…</p>
        ) : matches.length === 0 ? (
          <p className="px-3 py-4 text-center text-sm text-gray-500">
            {needle ? `No customer matches “${q.trim()}”.` : 'No customers on file.'}
          </p>
        ) : (
          matches.map((c) => {
            const selected = String(picked) === String(c.id);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setPicked(c.id)}
                className={`flex w-full items-center justify-between gap-2 border-l-2 px-3 py-2 text-left text-sm transition-colors ${
                  selected ? 'border-blue-500 bg-blue-50' : 'border-transparent hover:bg-gray-50'
                }`}
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium text-gray-900">
                    {c.name || c.display_name || `#${c.id}`}
                  </span>
                  {(c.billingAccount || c.billing_account) && (
                    <span className="block truncate text-xs text-gray-500">
                      {c.billingAccount || c.billing_account}
                    </span>
                  )}
                </span>
                {selected && <span className="flex-shrink-0 text-sm font-bold text-blue-600">✓</span>}
              </button>
            );
          })
        )}
      </div>

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => onSubmit(String(picked))}
          disabled={isPending || !picked}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {isPending ? 'Assigning…' : 'Assign to customer'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// Place the device at an address, chosen by ADDRESS.
//
// Was a numeric "Location ID" box — the same unusable pattern as the subscriber
// one. Restricted to the owning customer's addresses, because the API enforces
// that the device and the location belong to the same customer; offering the
// whole org's list would just produce a rejection.
function AssignLocationForm({ subscriberId, onSubmit, onCancel, isPending, error }) {
  const [picked, setPicked] = useState('');

  const locsQ = useQuery({
    queryKey: ['locations', 'for-device', subscriberId],
    queryFn: () => api.get('/locations'),
    enabled: Boolean(subscriberId),
  });
  const all = Array.isArray(locsQ.data)
    ? locsQ.data
    : Array.isArray(locsQ.data?.rows) ? locsQ.data.rows : [];
  const mine = all.filter((l) => (l.subscribers || []).some((x) => Number(x.id) === Number(subscriberId))
    || Number(l.subscriberId ?? l.subscriber_id) === Number(subscriberId));

  const label = (l) => {
    const line1 = l.addressLine1 || l.address_line1 || l.label || `Location #${l.id}`;
    const tail = [[l.city, l.state].filter(Boolean).join(', '), l.postal].filter(Boolean).join(' ');
    return tail ? `${line1} · ${tail}` : line1;
  };

  return (
    <div className="mt-3 rounded-md border border-blue-200 bg-blue-50/40 p-3">
      <label htmlFor="dev-loc" className="block text-xs font-medium text-gray-700">Service address</label>
      {!subscriberId ? (
        <p className="mt-1 text-sm text-gray-600">
          Assign this device to a customer first — a device is placed at one of their addresses.
        </p>
      ) : (
        <>
          <select
            id="dev-loc"
            value={picked}
            onChange={(e) => setPicked(e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">{locsQ.isLoading ? 'Loading addresses…' : '— pick an address —'}</option>
            {mine.map((l) => <option key={l.id} value={l.id}>{label(l)}</option>)}
          </select>
          {!locsQ.isLoading && mine.length === 0 && (
            <p className="mt-1 text-xs text-amber-700">
              This customer has no service address yet. Add one on their page first — E911 needs it.
            </p>
          )}
        </>
      )}

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => onSubmit(String(picked))}
          disabled={isPending || !picked}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {isPending ? 'Placing…' : 'Place device here'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// Assign a SIP account to a device port. The account is chosen from the
// device's owning subscriber (no blind numeric id), and the port is
// pre-filled from the device's GDMS provisioning when that account already
// appears on a line there — so binding Verso to reality is one click.
function AddSipAccountForm({ accounts, portByUsername, capacity, takenPorts = [], loadingAccounts, noSubscriber, onSubmit, onCancel, isPending, error }) {
  const [accountId, setAccountId] = useState('');
  const [position, setPosition] = useState('');

  // The port is a choice among the lines the phone HAS, not free text. Typing 6
  // into an HT802 used to be accepted here and refused by the GDMS push, which is
  // the wrong end of the process to find out.
  const slots = capacity?.slots || 0;
  const taken = new Set(takenPorts.map(Number));

  // When an account is picked, suggest the port GDMS already serves it on.
  function pickAccount(v) {
    setAccountId(v);
    const acct = accounts.find((a) => String(a.id) === String(v));
    const digits = String(acct?.username || '').replace(/[^0-9]/g, '');
    const suggested = digits && portByUsername[digits] != null ? portByUsername[digits] : null;
    if (suggested != null) setPosition(String(suggested));
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!accountId) return;
        onSubmit({
          sip_account_id: Number(accountId),
          position: position.trim() === '' ? null : Number(position),
        });
      }}
      className="mt-3 flex flex-wrap items-end gap-2 rounded-md border border-blue-200 bg-blue-50/40 p-3"
    >
      <div className="min-w-[200px] flex-1">
        <label className="block text-xs font-medium text-gray-700">SIP Account</label>
        <select
          value={accountId}
          onChange={(e) => pickAccount(e.target.value)}
          disabled={loadingAccounts || noSubscriber}
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50"
          autoFocus
        >
          <option value="">
            {noSubscriber ? 'Assign a subscriber first'
              : loadingAccounts ? 'Loading accounts…'
              : accounts.length === 0 ? 'No unbound accounts on this subscriber'
              : '— pick an extension —'}
          </option>
          {accounts.map((a) => {
            const digits = String(a.username || '').replace(/[^0-9]/g, '');
            const onGdms = digits && portByUsername[digits] != null;
            return (
              <option key={a.id} value={a.id}>
                {a.username || `#${a.id}`}{onGdms ? ` · GDMS port ${portByUsername[digits]}` : ''}
              </option>
            );
          })}
        </select>
      </div>
      <div className="min-w-[140px]">
        <label htmlFor="line-port" className="block text-xs font-medium text-gray-700">
          Line{capacity && <span className="ml-1 font-normal text-gray-400">of {capacity.slots}</span>}
        </label>
        {slots > 0 ? (
          <select
            id="line-port"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">— not set —</option>
            {Array.from({ length: slots }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {n}{taken.has(n) ? ' · in use' : ''}
              </option>
            ))}
          </select>
        ) : (
          // No capacity from the API (an uncatalogued model on an older payload):
          // fall back rather than offer an empty dropdown.
          <input
            id="line-port"
            type="number"
            min="1"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="1"
          />
        )}
      </div>
      <button
        type="submit"
        disabled={isPending || !accountId}
        className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
      >
        {isPending ? 'Assigning…' : 'Assign account'}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
      >
        Cancel
      </button>
      {error && <p className="w-full text-sm text-red-600">{error}</p>}
    </form>
  );
}

// ------- Section wrapper -------
function Section({ title, subtitle, children, actions }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

export default function DeviceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { role } = useAuth();
  const canOp   = canOperate(role); // subscriber + location assign/unassign
  const canAdm  = canAdmin(role);   // attach/remove SIP account, delete device

  const { data: device, isLoading, error } = useQuery({
    queryKey: ['device', id],
    queryFn: () => api.get(`/devices/${id}`),
  });

  // The device's owning subscriber and its SIP accounts — drives the attach
  // picker (ticket #7): the operator chooses from real accounts on this
  // subscriber instead of typing a numeric id blind.
  const deviceSubId = device?.subscriberId ?? device?.subscriber_id ?? null;
  const subAccountsQ = useQuery({
    queryKey: ['sip-accounts', 'subscriber', deviceSubId],
    queryFn: () => api.get('/sip-accounts', { subscriberId: deviceSubId }),
    enabled: Boolean(deviceSubId),
  });

  const [editSubscriber, setEditSubscriber] = useState(false);
  // Tabs + one kebab, matching the DID / subscriber / location pages.
  // In the URL, so a refresh stays on this tab and back leaves the page rather than
  // rewinding through tabs. See hooks/useHashTab.js.
  const [activeTab, setActiveTab] = useHashTab('overview', { valid: DEVICE_TABS });
  // Falls back to the global device list only when nothing said where we came from.
  const back = useBackTo('Devices', '/devices');
  const [editLocation, setEditLocation] = useState(false);
  const [addingAccount, setAddingAccount] = useState(false);
  const [confirmUnassign, setConfirmUnassign] = useState(false);
  // REBOOT. Kevin, 2026-08-22. It is the fix for a phone that believes it holds a registration the
  // PBX has lost — GDMS shows it online, the host has no contact, every call goes to voicemail.
  //
  // Two states, not one: a plain refusal (409) means the worker declined because the phone is on a
  // call or it could not tell, and that is a QUESTION to put to the operator rather than an error to
  // print. Anything else is a genuine failure.
  const [confirmReboot, setConfirmReboot] = useState(false);
  const [rebootBlocked, setRebootBlocked] = useState(null);
  const [rebootDone, setRebootDone] = useState(null);
  const [confirmDetachLoc, setConfirmDetachLoc] = useState(false);
  const [confirmRemoveAccount, setConfirmRemoveAccount] = useState(null); // sip_account_id or null
  const [confirmDelete, setConfirmDelete] = useState(false);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['device', id] });

  // Rename. Verso saves the name either way; `gdms` on the response says whether
  // the push landed, and is shown rather than swallowed — a name that is right
  // here and wrong in the GDMS console is exactly the drift worth surfacing.
  const [editName, setEditName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [namePush, setNamePush] = useState(null);
  const renameDevice = useMutation({
    mutationFn: (name) => api.patch(`/devices/${id}`, { name }),
    onSuccess: (res) => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      setEditName(false);
      setNamePush(res?.gdms ?? null);
    },
  });

  const assignSub = useMutation({
    mutationFn: (subId) => api.put(`/devices/${id}/subscriber/${subId}`),
    onSuccess: () => { invalidate(); setEditSubscriber(false); },
  });
  const unassignSub = useMutation({
    mutationFn: () => api.delete(`/devices/${id}/subscriber`),
    onSuccess: () => { invalidate(); setConfirmUnassign(false); },
  });

  // WHAT GDMS SAYS RIGHT NOW, as distinct from what we last pulled.
  //
  // The tab below renders `device.provisioning`, which is a CACHE filled by Devices → Sync
  // provisioning. On 2026-09-09 that cache said all three accounts were seated while GDMS's own
  // screen said one was assigned to no device at all — so the page agreed with me and disagreed
  // with reality, and I spent an hour on the wrong side of it.
  //
  // `isSynchronized` is the field that mattered most and was surfaced nowhere: it is the DEVICE's
  // answer to "have you applied this config", which is a different question from whether GDMS holds
  // it or whether Verso last pushed it.
  const gdmsLive = useQuery({
    queryKey: ['device', id, 'gdms-live'],
    queryFn: () => api.get(`/devices/${id}/gdms-live`),
    enabled: activeTab === 'gdms',
    retry: false,
  });

  const rebootDevice = useMutation({
    mutationFn: (force) => api.post(`/devices/${id}/reboot`, force ? { force: true } : {}),
    onSuccess: (res) => {
      setRebootBlocked(null);
      setConfirmReboot(false);
      setRebootDone(res?.note || 'GDMS accepted the reboot.');
      invalidate();
    },
    onError: (err) => {
      // A 409 carries the reason it declined, which the dialog turns into "reboot anyway?".
      const b = err?.body || null;
      if (err?.status === 409 && b) setRebootBlocked(b);
      else setRebootBlocked({ error: err?.message || 'The reboot failed.', fatal: true });
    },
  });
  const attachLoc = useMutation({
    mutationFn: (locId) => api.put(`/devices/${id}/location/${locId}`),
    onSuccess: () => { invalidate(); setEditLocation(false); },
  });
  const detachLoc = useMutation({
    mutationFn: () => api.delete(`/devices/${id}/location`),
    onSuccess: () => { invalidate(); setConfirmDetachLoc(false); },
  });
  const addAccount = useMutation({
    mutationFn: (body) => api.post(`/devices/${id}/sip-accounts`, body),
    onSuccess: () => { invalidate(); setAddingAccount(false); },
  });
  const removeAccount = useMutation({
    mutationFn: (accountId) => api.delete(`/devices/${id}/sip-accounts/${accountId}`),
    onSuccess: () => { invalidate(); setConfirmRemoveAccount(null); },
  });
  // Record the lines GDMS already has. Never deletes: a binding GDMS doesn't
  // mention comes back as drift for a human to decide about.
  const adoptLines = useMutation({
    mutationFn: () => api.post(`/devices/${id}/adopt-bindings`, {}),
    onSuccess: () => invalidate(),
  });
  const deleteDevice = useMutation({
    mutationFn: () => api.delete(`/devices/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      navigate('/devices');
    },
  });

  const breadcrumb = (
    <div className="text-sm text-gray-500">
      <Link to={back.href} className="text-blue-600 hover:underline">{back.label}</Link>
      <span className="mx-2">/</span>
      <span className="font-mono">{device?.mac || id}</span>
    </div>
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        {breadcrumb}
        <LoadingSpinner message="Loading device..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        {breadcrumb}
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error?.body?.error || error.message || 'Failed to load device'}
        </div>
      </div>
    );
  }

  if (!device) {
    return (
      <div className="space-y-6">
        {breadcrumb}
        <div className="rounded-md border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
          Device not found.
        </div>
      </div>
    );
  }

  const mac = device.mac || '—';
  const model = device.model || 'unknown';
  const name = device.name || '(unnamed)';
  const subId = device.subscriberId ?? device.subscriber_id;
  const locId = device.locationId ?? device.location_id;
  const gdmsDeviceId = device.gdmsDeviceId ?? device.gdms_device_id;
  const sipAccounts = Array.isArray(device.sipAccounts) ? device.sipAccounts : [];
  // { slots, known } from the API's model table — the same one that caps the GDMS
  // write, so the page can't promise a line the push would refuse.
  const capacity = device.capacity || null;

  // What GDMS says this phone is loaded with: extension → port.
  //
  // All four list shapes, because GDMS names the list per device family and the
  // read shape isn't the write shape. This only read fxsPortList and lineList,
  // which is why a GXP2140 with two accounts bound showed its line as "—": IP
  // phones report sipAccountInfoList, and it was being ignored.
  // Plain computation, not a hook — this sits below the loading/404 early
  // returns, and a hook here would be called conditionally.
  const gdmsPortByUsername = (() => {
    const acct = device.provisioning?.account;
    const out = {};
    const take = (list, keys) => {
      for (const p of (Array.isArray(list) ? list : [])) {
        const u = String(p?.sipUserId || '').replace(/[^0-9]/g, '');
        if (!u || out[u] != null) continue;
        for (const k of keys) {
          if (p?.[k] != null && p[k] !== '') { out[u] = Number(p[k]); break; }
        }
      }
    };
    take(acct?.sipAccountInfoList,   ['account', 'accountIndex']);
    take(acct?.sipAccountDeviceList, ['accountIndex', 'account']);
    take(acct?.fxsPortList,          ['account', 'fxsPortNumber']);
    take(acct?.lineList,             ['line', 'account']);
    return out;
  })();

  // Lines GDMS holds that Verso has no binding for. This is the disconnect that
  // stopped provisioning: the header counts lines from the snapshot while every
  // other view reads the join table, so one phone read "2" and "none" at once.
  const unadoptedLines = (() => {
    const bound = new Set(sipAccounts.map((a) => String(a.username || '').replace(/[^0-9]/g, '')));
    return Object.entries(gdmsPortByUsername)
      .filter(([u]) => !bound.has(u))
      .map(([username, port]) => ({ username, port }));
  })();

  // ...and the OTHER drift, which had no report at all until 2026-08-04: a line Verso HAS
  // bound, at a different port than GDMS holds it on.
  //
  // Found live on EC74D7DC7552. GDMS had 17208644320 on account 2; Verso recorded port 3.
  // Nothing was "unadopted" — the username was bound — so the banner below never rendered
  // and "Record what GDMS has" was unreachable, while Verso refused to bind a new line to
  // port 3 on the strength of a record only it believed. A GDMS push renumbers the other
  // accounts on a DECT base (#231), so this is the drift that device produces routinely.
  //
  // GDMS owns the binding and Verso adopts it (project_gdms_binding_authority), so the
  // fix is the same button — `adoptGdmsBindings` already reports `repositioned`. Only the
  // trigger was missing.
  const mispositionedLines = (() => {
    const out = [];
    for (const a of sipAccounts) {
      const u = String(a.username || '').replace(/[^0-9]/g, '');
      const theirs = gdmsPortByUsername[u];
      // Only when GDMS has an opinion. A line GDMS does not mention is not drift — it is
      // a binding Verso holds alone, which the adopt path deliberately never deletes.
      if (!u || theirs == null) continue;
      if (a.position != null && Number(a.position) !== Number(theirs)) {
        out.push({ username: u, versoPort: a.position, gdmsPort: theirs });
      }
    }
    return out;
  })();

  const hasBindingDrift = unadoptedLines.length > 0 || mispositionedLines.length > 0;

  // A "!" flags something an operator would want to fix. A device with no
  // customer can't be provisioned, and one with no address has no 911 dispatch
  // point — both worth seeing without opening the tab.
  // "Overview" and "Assignment" rendered the SAME two sections: both bodies were
  // guarded by a condition that matched either tab, so switching between them
  // changed nothing on screen. One tab now, and the flag that lived on
  // Assignment moves onto it.
  const TABS = [
    { id: 'overview',   label: 'Overview',
      flag: !subId || !locId,
      flagTitle: !subId ? 'No customer assigned' : 'No service address — E911 needs one' },
    { id: 'lines',      label: 'Lines', count: sipAccounts.length },
    { id: 'gdms',       label: 'GDMS' },
  ];

  // Every action in one kebab, top-right, destructive last behind a divider.
  const actions = [
    ...(canOp && !subId ? [{
      label: 'Assign to a customer',
      onClick: () => { setActiveTab('overview'); setEditSubscriber(true); assignSub.reset(); },
    }] : []),
    ...(canOp && subId ? [{
      label: locId ? 'Change service address' : 'Place at an address',
      onClick: () => { setActiveTab('overview'); setEditLocation(true); attachLoc.reset(); },
    }] : []),
    ...(canOp && subId ? [{
      label: 'Assign a line',
      onClick: () => { setActiveTab('lines'); setAddingAccount(true); addAccount.reset(); },
    }] : []),
    ...(canOp && subId ? [
      { divider: true },
      { label: 'Unassign from customer', danger: true, onClick: () => { setConfirmUnassign(true); unassignSub.reset(); } },
    ] : []),
    ...(canOp && locId ? [{
      label: 'Remove from address', danger: true, onClick: () => { setConfirmDetachLoc(true); detachLoc.reset(); },
    }] : []),
    // Only where GDMS could act. A Calix is not in GDMS, and offering a button that always 400s
    // teaches an operator that the menu lies.
    ...(canAdm && device.mac && !device.selfProvisioning ? [
      { divider: true },
      {
        label: 'Reboot this phone',
        onClick: () => { setRebootBlocked(null); setRebootDone(null); setConfirmReboot(true); rebootDevice.reset(); },
      },
    ] : []),
    ...(canAdm ? [
      ...(canOp && (subId || locId) ? [] : [{ divider: true }]),
      { label: 'Delete from inventory', danger: true, onClick: () => { setConfirmDelete(true); deleteDevice.reset(); } },
    ] : []),
  ];

  return (
    <div className="space-y-6">
      {breadcrumb}

      {/* Header */}
      <div className="rounded-lg border border-gray-200 bg-white p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-mono text-xl font-bold text-gray-900">{mac}</h1>
              {/* "in inventory" was hard-coded, so a phone sitting at a customer's
                  address with two lines on it still read as spare stock. Inventory
                  means nobody owns it. */}
              {subId
                ? <StatusBadge status="active" label="in service" />
                : <StatusBadge status="unknown" label="in inventory" />}
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {model} {name !== '(unnamed)' && <>· {name}</>}
              {gdmsDeviceId && <> · GDMS #{gdmsDeviceId}</>}
            </p>
          </div>
          {(canOp || canAdm) && (
            <div className="flex-shrink-0">
              <ActionMenu title={`Actions for ${mac}`} items={actions} />
            </div>
          )}
        </div>
        {/* Model is NOT here: it is already in the subtitle above and again under
            Hardware. Dropping it lets the four things an operator actually
            changes — name, owner, address, lines — sit on one row instead of
            spilling Lines onto a second. */}
        <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Field
            label="Name"
            value={editName ? (
              <form
                className="flex items-center gap-1"
                onSubmit={(e) => { e.preventDefault(); renameDevice.mutate(nameDraft); }}
              >
                <input
                  autoFocus type="text" value={nameDraft} maxLength={64}
                  onChange={(e) => setNameDraft(e.target.value)}
                  placeholder="e.g. Front Desk"
                  className="w-40 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <button type="submit" disabled={renameDevice.isPending}
                        className="rounded-md bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                  {renameDevice.isPending ? 'Saving…' : 'Save'}
                </button>
                <button type="button" onClick={() => { setEditName(false); renameDevice.reset(); }}
                        className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50">
                  Cancel
                </button>
              </form>
            ) : (
              <span className="flex items-center gap-2">
                {device?.name || <span className="text-gray-300">—</span>}
                {canOp && (
                  <button
                    onClick={() => { setNameDraft(device?.name || ''); setNamePush(null); setEditName(true); }}
                    className="text-xs font-medium text-blue-600 hover:underline"
                  >
                    {device?.name ? 'Edit' : 'Add'}
                  </button>
                )}
              </span>
            )}
          />
          {/* Customer and Address were shown here read-only AND repeated below as
              two full-width cards with their own buttons — the same two facts
              three times between them. They are settings, so they are editable
              where they are read. */}
          <Field label="Customer" value={
            <span className="flex flex-wrap items-center gap-x-2">
              {subId
                ? <Link to={`/subscribers/${subId}`} className="text-blue-600 hover:underline">{device?.subscriberName || `#${subId}`}</Link>
                : <span className="text-gray-400">Unassigned</span>}
              {canOp && !editSubscriber && (
                subId
                  ? <button onClick={() => { setConfirmUnassign(true); unassignSub.reset(); }}
                            className="text-xs font-medium text-red-600 hover:underline">Unassign</button>
                  : <button onClick={() => { setEditSubscriber(true); assignSub.reset(); }}
                            className="text-xs font-medium text-blue-600 hover:underline">Assign</button>
              )}
            </span>
          } />
          <Field label="Address" value={
            <span className="flex flex-wrap items-center gap-x-2">
              {locId
                ? <Link to={`/locations/${locId}`} className="text-blue-600 hover:underline">{device?.locationLabel || device?.location_label || `#${locId}`}</Link>
                : <span className="text-amber-700" title="E911 needs a service address">Not set</span>}
              {canOp && !editLocation && (
                locId
                  ? <button onClick={() => { setConfirmDetachLoc(true); detachLoc.reset(); }}
                            className="text-xs font-medium text-red-600 hover:underline">Remove</button>
                  : <button onClick={() => { setEditLocation(true); attachLoc.reset(); }}
                            className="text-xs font-medium text-blue-600 hover:underline">Set</button>
              )}
            </span>
          } />
          {/* Lines used OF lines the model has — "2" alone doesn't say whether the
              phone is full. Capacity comes from the API so one table governs both
              the picker and the GDMS write. */}
          <Field
            label="Lines"
            value={sipAccounts.length === 0
              ? <span className="text-gray-300">none</span>
              : capacity
                ? <>
                    {sipAccounts.length} of {capacity.slots}
                    {!capacity.known && <span className="ml-1 text-xs text-gray-400" title="This model isn’t catalogued — 4 assumed">(assumed)</span>}
                  </>
                : sipAccounts.length}
          />
        </dl>

        {/* The pickers open here, directly under the field they change, rather
            than in a card further down the page. */}
        {editSubscriber && (
          <AssignSubscriberForm
            onSubmit={(v) => assignSub.mutate(v)}
            onCancel={() => { setEditSubscriber(false); assignSub.reset(); }}
            isPending={assignSub.isPending}
            error={assignSub.isError
              ? (assignSub.error?.body?.error || assignSub.error?.message || 'Assign failed')
              : null}
          />
        )}
        {editLocation && (
          <AssignLocationForm
            subscriberId={subId}
            onSubmit={(v) => attachLoc.mutate(v)}
            onCancel={() => { setEditLocation(false); attachLoc.reset(); }}
            isPending={attachLoc.isPending}
            error={attachLoc.isError
              ? (attachLoc.error?.body?.error || attachLoc.error?.message || 'Place failed')
              : null}
          />
        )}

        {/* The push outcome. Saying only "saved" would hide a name that is right
            in Verso and wrong in the GDMS console — which is the drift the
            operator is renaming the device to avoid in the first place. */}
        {renameDevice.isError && (
          <p className="mt-3 text-sm text-red-700">
            {renameDevice.error?.body?.error || renameDevice.error?.message || 'Rename failed'}
          </p>
        )}
        {namePush?.ok === true && (
          <p className="mt-3 text-sm text-green-700">Renamed, and confirmed in GDMS.</p>
        )}
        {namePush?.attempted && namePush.ok === false && (
          <p className="mt-3 text-sm font-medium text-red-800">
            Saved in Verso, but GDMS did not take it: {namePush.error}
          </p>
        )}
        {namePush?.attempted === false && (
          <p className="mt-3 text-sm text-gray-500">Saved. {namePush.reason}.</p>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex flex-wrap gap-6" role="tablist">
          {TABS.map((t) => {
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(t.id)}
                className={`inline-flex items-center gap-2 border-b-2 px-1 pb-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.label}
                {t.count > 0 && (
                  <span className="rounded-full bg-gray-100 px-1.5 text-xs font-medium text-gray-600">{t.count}</span>
                )}
                {t.flag && (
                  <span title={t.flagTitle}
                        className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-amber-100 text-[10px] font-bold text-amber-700">!</span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Which network this handset is on. With each line's PBX it decides the address the
          phone registers to, so it sits on the overview rather than buried in provisioning. */}
      {activeTab === 'overview' && <DeviceSegmentCard device={device} canEdit={canOp} />}

      {/* Which line rings on which handset — only meaningful for a multi-handset base. */}
      {activeTab === 'overview' && /^(DP|WP)/i.test(device.model || '') && (
        <HandsetMatrix device={device} canEdit={canOp} />
      )}

      {/* Provisioning (pulled from GDMS) — its own tab. */}
      {activeTab === 'gdms' && (() => {
        const prov = device.provisioning;
        const syncedAt = device.provisioningSyncedAt || device.provisioning_synced_at;
        if (!prov && !syncedAt) return null;
        return (
          <Section
            title="GDMS Provisioning"
            subtitle={syncedAt
              ? `Last pulled ${fmtDateTime(syncedAt)} — from Devices → Sync provisioning.`
              : 'Not pulled yet.'}
          >
            {/* LIVE, above the cache, because the cache is what misled us. */}
            {gdmsLive.data?.gdms && (
              <div className="mt-3 grid gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm sm:grid-cols-3">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-gray-600">In GDMS now</span>
                  <span className={gdmsLive.data.gdms.status === 1 ? 'text-green-700' : 'text-red-700'}>
                    {gdmsLive.data.gdms.status === 1 ? 'online' : 'offline'}
                  </span>
                </div>
                <div className="flex items-baseline justify-between gap-2">
                  {/* THE ONE THAT WOULD HAVE SAVED THE HOUR. "Config applied" and not
                      "synchronised", because the operator question is whether the PHONE has it. */}
                  <span className="text-gray-600">Config applied</span>
                  <span className={gdmsLive.data.gdms.isSynchronized === 1 ? 'text-green-700' : 'text-amber-700'}>
                    {gdmsLive.data.gdms.isSynchronized === 1
                      ? 'yes'
                      : 'NOT YET — the phone has not taken it; a reboot makes it look'}
                  </span>
                </div>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-gray-600">Accounts</span>
                  <span className={gdmsLive.data.gdms.accountStatus === 1 ? 'text-green-700' : 'text-red-700'}>
                    {gdmsLive.data.gdms.accountStatus === 1 ? 'registered' : 'not registered'}
                  </span>
                </div>
                {gdmsLive.data.gdms.syncFailureMsg && (
                  <p className="sm:col-span-3 text-xs text-red-700">{gdmsLive.data.gdms.syncFailureMsg}</p>
                )}
                {/* Which account sits on which slot and PROFILE, live. A profile is a server
                    binding, so two accounts on one server split across profiles is a fault you can
                    only see here. */}
                {(gdmsLive.data.accounts?.sipAccountInfoList || []).length > 0 && (
                  <ul className="sm:col-span-3 mt-1 space-y-0.5 border-t border-gray-100 pt-2 text-xs text-gray-600">
                    {gdmsLive.data.accounts.sipAccountInfoList.map((a) => (
                      <li key={a.sipAccountId}>
                        slot {a.account} · <span className="font-mono">{a.sipUserId}</span> →{' '}
                        <span className="font-mono">{a.sipServer}</span> · profile {a.profile}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {gdmsLive.isError && (
              <p className="mt-3 text-xs text-gray-500">Could not read GDMS live: {gdmsLive.error?.body?.error || gdmsLive.error?.message}</p>
            )}
            {/* "line": null reads as "this base has no handsets" — and the handset-line
                matrix is what decides whose phone rings for which number. When the read
                failed, say so, or the next person designs around an emptiness that is
                really a gap in our own asking. */}
            {prov?.lineError && (
              <p className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                The handset-line matrix could not be read from GDMS, so <code>line</code> below
                is null because we could not ask — not because this base has no handsets:{' '}
                {prov.lineError}
              </p>
            )}
            {prov ? (
              <pre className="mt-3 max-h-96 overflow-auto rounded-md border border-gray-200 bg-gray-50 p-3 font-mono text-xs text-gray-700">
                {JSON.stringify({ account: prov.account ?? null, line: prov.line ?? null }, null, 2)}
              </pre>
            ) : (
              <p className="mt-3 text-sm text-gray-400">No provisioning cached for this device.</p>
            )}
          </Section>
        );
      })()}

      {/* Overview now carries the hardware facts. None of these were on the page
          anywhere — the serial in particular, which GDMS requires alongside the
          MAC to identify the unit, so an operator had no way to see whether Verso
          had it. */}
      {activeTab === 'overview' && (
        <Section title="Hardware" subtitle="What this unit is, and where Verso learned about it.">
          <dl className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Field label="MAC" value={<span className="font-mono">{mac}</span>} />
            <Field label="Model" value={model} />
            <Field label="Serial" value={device?.sn
              ? <span className="font-mono">{device.sn}</span>
              : <span className="text-gray-300" title="Filled in from GDMS the first time the device is renamed">—</span>} />
            <Field label="Type" value={device?.deviceType || device?.device_type || <span className="text-gray-300">—</span>} />
            <Field label="GDMS device" value={gdmsDeviceId
              ? <span className="font-mono">#{gdmsDeviceId}</span>
              : <span className="text-gray-300">—</span>} />
            <Field label="Added by" value={device?.importSource || device?.import_source || 'by hand'} />
            {(device?.fsan) && <Field label="Calix FSAN" value={<span className="font-mono">{device.fsan}</span>} />}
            <Field label="Provisioning pulled" value={(device?.provisioningSyncedAt || device?.provisioning_synced_at)
              ? fmtDateTime(device.provisioningSyncedAt || device.provisioning_synced_at)
              : <span className="text-gray-300">never</span>} />
          </dl>
        </Section>
      )}

      {/* Lines (SIP accounts bound to this device's ports) */}
      {activeTab === 'lines' && (
      <Section
        title="Lines"
        subtitle={sipAccounts.length === 0
          ? 'No accounts bound. Assign one to route calls through this device.'
          : `${sipAccounts.length} account${sipAccounts.length === 1 ? '' : 's'} bound.`}
        actions={canAdm && !addingAccount && (
          <button
            onClick={() => { setAddingAccount(true); addAccount.reset(); }}
            className="rounded-md border border-blue-300 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50"
          >
            Assign account
          </button>
        )}
      >
        {/* The disconnect, named where it shows up. GDMS is what the phone
            actually loads, so if it holds a line Verso has no binding for, the
            page says so and offers to record it instead of leaving two pages
            disagreeing about the same phone. */}
        {hasBindingDrift && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {unadoptedLines.length > 0 && (
              <p>
                GDMS has {unadoptedLines.length} line{unadoptedLines.length === 1 ? '' : 's'} on this
                device that Verso hasn&rsquo;t recorded:{' '}
                <span className="font-mono">
                  {unadoptedLines.map((l) => `${l.username}${l.port != null ? ` (line ${l.port})` : ''}`).join(', ')}
                </span>
                . Until it&rsquo;s recorded, provisioning can&rsquo;t see a phone on those extensions.
              </p>
            )}
            {mispositionedLines.length > 0 && (
              <p className={unadoptedLines.length > 0 ? 'mt-2' : undefined}>
                {mispositionedLines.length === 1 ? 'A line is' : `${mispositionedLines.length} lines are`}{' '}
                recorded on a different port than GDMS has{' '}
                {mispositionedLines.length === 1 ? 'it' : 'them'} on:{' '}
                <span className="font-mono">
                  {mispositionedLines
                    .map((l) => `${l.username} (Verso port ${l.versoPort}, GDMS ${l.gdmsPort})`)
                    .join(', ')}
                </span>
                . GDMS is what the phone loads, so its port is the real one — and until Verso
                agrees it will refuse to bind another line to a port that is actually free.
              </p>
            )}
            {canAdm && (
              <button
                onClick={() => adoptLines.mutate()}
                disabled={adoptLines.isPending}
                className="mt-2 rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
              >
                {adoptLines.isPending ? 'Recording…' : 'Record what GDMS has'}
              </button>
            )}
          </div>
        )}
        {/* Partial success is not success — say what it skipped and why. */}
        {adoptLines.isSuccess && (() => {
          const r = adoptLines.data || {};
          const dev = r.devices?.[0] || {};
          const skipped = dev.skipped || [];
          const created = r.created || 0;
          // Built as whole sentences, not interleaved JSX — a count spliced between
          // text nodes is unreadable to anything that searches the rendered text.
          const headline = `Recorded ${created} line${created === 1 ? '' : 's'}`
            + (r.repositioned ? `, moved ${r.repositioned} to the line GDMS says` : '')
            + (r.already ? `, ${r.already} already matched` : '')
            + '.';
          const skippedLine = skipped.length
            ? `Skipped ${skipped.length}: ${skipped
                .map((s) => `${s.username || 'this device'} — ${s.detail || s.reason}`).join('; ')}.`
            : null;
          const driftLine = r.drift?.length
            ? `Verso also holds ${r.drift.length} line${r.drift.length === 1 ? '' : 's'} GDMS `
              + `doesn’t mention (${r.drift.map((d) => d.username).join(', ')}) — left alone; `
              + 'removing a line takes a phone off service.'
            : null;
          return (
            <div className={`mt-3 rounded-md border px-4 py-2 text-sm ${
              skipped.length ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-green-200 bg-green-50 text-green-800'
            }`}>
              <p>
                {headline}
                <button onClick={() => adoptLines.reset()} className="ml-2 underline hover:no-underline">Dismiss</button>
              </p>
              {skippedLine && <p className="mt-1">{skippedLine}</p>}
              {driftLine && <p className="mt-1">{driftLine}</p>}
            </div>
          );
        })()}
        {adoptLines.isError && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
            {adoptLines.error?.body?.error || adoptLines.error?.message || 'Could not record the GDMS lines'}
          </div>
        )}
        {addingAccount && (() => {
          // Candidate accounts = the subscriber's accounts not already bound
          // to this device. Port suggestions come from the device's GDMS
          // provisioning (sipUserId → port).
          const boundIds = new Set(sipAccounts.map((a) => a.sip_account_id));
          const candidates = (Array.isArray(subAccountsQ.data) ? subAccountsQ.data : [])
            .filter((a) => !boundIds.has(a.id));
          return (
            <AddSipAccountForm
              accounts={candidates}
              portByUsername={gdmsPortByUsername}
              capacity={capacity}
              takenPorts={sipAccounts.map((a) => a.position).filter((p) => p != null)}
              loadingAccounts={subAccountsQ.isLoading}
              noSubscriber={!deviceSubId}
              onSubmit={(v) => addAccount.mutate(v)}
              onCancel={() => { setAddingAccount(false); addAccount.reset(); }}
              isPending={addAccount.isPending}
              error={addAccount.isError
                ? (addAccount.error?.body?.error || addAccount.error?.message || 'Assign failed')
                : null}
            />
          );
        })()}
        {sipAccounts.length > 0 && (
          <div className="mt-3 overflow-hidden rounded-md border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-500">
                <tr>
                  <th className="px-3 py-2">Line</th>
                  <th className="px-3 py-2">Username</th>
                  <th className="px-3 py-2">Subscriber</th>
                  {/* No host column — the DID owns it; see the number's page. */}
                  <th className="px-3 py-2">Status</th>
                  {canAdm && <th className="px-3 py-2" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sipAccounts.map((a) => (
                  <tr key={a.sip_account_id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-gray-600">
                      {a.position ?? '—'}
                    </td>
                    <td className="px-3 py-2 font-mono">{a.username || `#${a.sip_account_id}`}</td>
                    <td className="px-3 py-2">
                      {a.subscriber_id ? (
                        <Link to={`/subscribers/${a.subscriber_id}`} className="text-blue-600 hover:underline">
                          {a.subscriber_name || `#${a.subscriber_id}`}
                        </Link>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge
                        status={a.status === 'active' ? 'active' : 'inactive'}
                        label={a.status || 'unknown'}
                      />
                    </td>
                    {canAdm && (
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => setConfirmRemoveAccount(a.sip_account_id)}
                          className="text-xs font-medium text-red-600 hover:underline"
                        >
                          Remove
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      )}

      {/* Delete lives in the header kebab now — no danger zone to scroll past.
          Errors surface here so a failed delete isn't lost with the closed menu. */}
      {deleteDevice.isError && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {deleteDevice.error?.body?.error || deleteDevice.error?.message || 'Delete failed'}
        </p>
      )}

      {/* Confirm modals */}
      {confirmReboot && (
        <ConfirmDeleteDialog
          title={rebootBlocked && !rebootBlocked.fatal ? 'Reboot anyway?' : 'Reboot this phone'}
          body={
            rebootBlocked?.fatal ? rebootBlocked.error
              // THE WORKER'S REASON, VERBATIM. It knows whether a call is up or whether it could
              // not tell, and those are different risks — paraphrasing them here would be a second
              // opinion about somebody's live call.
              : rebootBlocked ? rebootBlocked.error
                : 'The phone drops its registration and comes back in a minute or two. Calls in '
                  + 'progress on it are checked first and will stop this if there are any.'
          }
          confirmLabel={rebootBlocked && !rebootBlocked.fatal ? 'Reboot anyway' : 'Reboot'}
          destructive={Boolean(rebootBlocked && !rebootBlocked.fatal)}
          onConfirm={() => rebootDevice.mutate(Boolean(rebootBlocked && !rebootBlocked.fatal))}
          onCancel={() => { setConfirmReboot(false); setRebootBlocked(null); rebootDevice.reset(); }}
          isPending={rebootDevice.isPending}
        />
      )}
      {rebootDone && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
          {rebootDone}
          <button
            type="button"
            onClick={() => setRebootDone(null)}
            className="ml-2 text-xs text-blue-700 underline"
          >
            dismiss
          </button>
        </div>
      )}
      {confirmUnassign && (
        <ConfirmDeleteDialog
          title="Unassign from Subscriber"
          body="This returns the device to the org pool and detaches every currently-bound SIP account so it can be reassigned cleanly."
          confirmLabel="Unassign"
          destructive={false}
          onConfirm={() => unassignSub.mutate()}
          onCancel={() => { setConfirmUnassign(false); unassignSub.reset(); }}
          isPending={unassignSub.isPending}
        />
      )}
      {confirmDetachLoc && (
        <ConfirmDeleteDialog
          title="Remove Location Placement"
          body="This clears the device's current physical location. E911 dispatch will be unavailable until a new location is assigned."
          confirmLabel="Remove"
          destructive={false}
          onConfirm={() => detachLoc.mutate()}
          onCancel={() => { setConfirmDetachLoc(false); detachLoc.reset(); }}
          isPending={detachLoc.isPending}
        />
      )}
      {confirmRemoveAccount != null && (
        <ConfirmDeleteDialog
          title="Detach SIP Account"
          body={`Detach SIP account #${confirmRemoveAccount} from this device. The account itself is not deleted — only its binding to this device.`}
          confirmLabel="Detach"
          destructive={false}
          onConfirm={() => removeAccount.mutate(confirmRemoveAccount)}
          onCancel={() => { setConfirmRemoveAccount(null); removeAccount.reset(); }}
          isPending={removeAccount.isPending}
        />
      )}
      {confirmDelete && (
        <ConfirmDeleteDialog
          title="Delete Device"
          body={`Soft-delete ${mac} from Verso inventory. All bindings will be cleared. This cannot be undone from the dashboard.`}
          confirmLabel="Delete Device"
          onConfirm={() => deleteDevice.mutate()}
          onCancel={() => { setConfirmDelete(false); deleteDevice.reset(); }}
          isPending={deleteDevice.isPending}
        />
      )}
    </div>
  );
}
